<script setup>

const route = useRoute()
const url = useRequestURL()

const { data: album } = await useAPIFetch(`Album/${route.params.slug}`)

if (!album.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
		fatal: true
  })
}

const about = album.value.body.find(item => item.specific === 'AboutAlbum')
const albumTrackList = album.value.body.find(item => item.component === 'AlbumTracklist')
const relatedList = album.value.body.find(item => item.component === 'RelatedList')
const participants = album.value.body.find(item => item.component === 'AlbumParticipants')
const awards = album.value.body.find(item => item.specific === 'Awards')
const reviews = album.value.body.find(item => item.specific === 'Reviews')

useHeadData({
	title: album.value.meta.title,
	description: album.value.meta.description,
	author: album.value.meta.author,
	copyright: album.value.meta.copyright,
	keywords: album.value.meta.keywords,
	og: album.value.meta.og,
	url: url.href
})

</script>

<template>
	<div class="container">
		<div class="mb-10 xl:mb-20">
			<h1 class="text-red">{{ album.attrs.artistdisplay }}</h1>
			<h2 class="h1 dark:text-light">{{ album.attrs.title }}</h2>
		</div>
		<div class="grid grid-cols-12 gap-8">
			<div class="col-span-12 xl:col-span-3">
				<AlbumInfo :albumInfo="album.attrs" :participants="participants?.participants" />
			</div>
			<ClientOnly>
				<div class="col-span-12 xl:col-start-5 xl:col-span-8">
					<div v-if="about" class="mb-16">
						<h3 class="dark:text-light text-dark mb-6">{{ about.title }}</h3>
						<div class="dark:text-light text-dark xl:text-lg wtext" v-html="about.content.replace(/\n/g, '<br>')" />
					</div>
					<div v-if="awards && awards.awards.length" class="mb-16">
						<h3 class="dark:text-light text-dark mb-6">{{ awards.title }}</h3>
						<ul class="dark:text-light text-lg list-disc pl-6">
							<li v-for="(award, index) in awards.awards" :key="index">
								<a class="link" :href="award.url" target="_blank">{{ award.name }}</a>
								<span class="inline-block ml-1.5">({{ award.year }})</span>
							</li>
						</ul>
					</div>
					<div v-if="reviews" class="mb-16 reviews">
						<h3 class="dark:text-light text-dark mb-6">{{ reviews.title }}</h3>
						<div class="dark:text-light text-dark xl:text-lg" v-html="reviews.content" />
					</div>
					<!-- tracklist -->
					<AlbumTracklist v-if="albumTrackList" :tracklist-id="albumTrackList.id" :album-url="album.attrs.url" :format="album.attrs.format" class="mb-20" />
					<!-- podobne tituly -->
					<div v-if="relatedList" class="mb-20 mt-24">
						<h3 class="dark:text-light text-dark mb-8">{{ relatedList.title }}</h3>
						<div class="grid grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-8">
							<div v-for="album in relatedList.items" :key="album.id">
								<NuxtLinkLocale class="block aspect-square bg-cover bg-no-repeat bg-center transition-all hover:scale-105" :style="{ backgroundImage: `url(${album.attrs.image.url})` }" :to="{ name: 'album-slug', params: { slug: album.id } }">
									<span class="invisible">{{ album.attrs.title }}</span>
								</NuxtLinkLocale>
							</div>
						</div>
					</div>
				</div>
			</ClientOnly>
		</div>
	</div>
</template>

<style lang="scss" scoped>

.reviews {
	:deep(p:last-child strong) {
		border-bottom: none;
	}
}

.wtext {
	:deep(p) {
		margin-bottom: 18px;

		&:last-child {
			margin-bottom: 0;
		}
	}
}

:deep(.reviews strong) {
	display: block;
	margin-top: 24px;
	padding-bottom: 40px;
	border-bottom: 1px solid theme('colors.light');
}

.light {
	.reviews {
		:deep(strong) {
			border-bottom: 1px solid theme('colors.dark8');
		}
	}
}

</style>